import React, { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';

import {
  ArrowDownIcon,
  ArrowUpIcon,
  TrophyIcon,
} from '../../../assets/svg/SVGComponents';
import TournamentRanking, {
  IRankingRow,
} from '../../../components/TournamentRanking';
import { IBoardSnapshot } from '../../../dtos/IBoardSnapshot';
import { IBoardDiff } from '../logic/diff';
import { groupKey, groupTitle } from '../logic/labels';
import { pageOfTeam, paginateGroups } from '../logic/paginate';
import {
  Badge,
  Centered,
  GroupChips,
  IconText,
  PageDot,
  PageNav,
  PositionDisc,
  RankingArea,
} from '../styles';
import AnimatedPoints from './AnimatedPoints';

/** Trophy é 32×32: stroke 2 equivale ao 1.5 dos ícones de 24. */
const trophyTitle = (text: string): React.ReactNode => (
  <IconText>
    <TrophyIcon strokeWidth={2} />
    {text}
  </IconText>
);

export const ROTATE_EVERY_MS = 12_000;
/** Quanto tempo os selos (▲2, NOVO LÍDER) e o destaque ficam visíveis. */
export const EFFECTS_VISIBLE_MS = 6_000;

interface IRankingBoardProps {
  snapshot: IBoardSnapshot;
  effects: IBoardDiff;
  effectsKey: number;
  /** Equipe do banner atual: a coluna pula para o grupo dela (FE-11). */
  focusTeamId: string | null;
  rowsPerPage: number;
  isMobile: boolean;
}

/**
 * Coluna de classificação do telão.
 *
 * - TV: grupos paginados e alternados a cada ~12s (FE-03); durante um banner,
 *   fica parada no grupo da equipe que pontuou (FE-11).
 * - Celular: um grupo por vez, escolhido por toque, sem rotação (FE-20).
 * - Depois de cada aplicação: contador de pontos (FE-12), reordenação animada
 *   com ▲/▼ (FE-13) e selo de novo líder (FE-14).
 */
const RankingBoard: React.FC<IRankingBoardProps> = ({
  snapshot,
  effects,
  effectsKey,
  focusTeamId,
  rowsPerPage,
  isMobile,
}) => {
  const pages = useMemo(
    () => paginateGroups(snapshot.ranking, rowsPerPage),
    [snapshot.ranking, rowsPerPage],
  );
  const [pageIndex, setPageIndex] = useState(0);
  const [mobileGroup, setMobileGroup] = useState<string | null>(null);
  const [fresh, setFresh] = useState(false);
  const reduceMotion = useReducedMotion();

  // Mostra os efeitos da última aplicação por alguns segundos.
  useEffect(() => {
    if (effectsKey === 0) return undefined;
    setFresh(true);
    const timer = setTimeout(() => setFresh(false), EFFECTS_VISIBLE_MS);
    return () => clearTimeout(timer);
  }, [effectsKey]);

  // Foco no grupo da equipe do banner, ou de quem acabou de mudar.
  useEffect(() => {
    const target =
      focusTeamId ??
      (effectsKey > 0
        ? (effects.newLeaders[0] ?? Object.keys(effects.scoreChanges)[0])
        : undefined);
    if (!target) return;

    const index = pageOfTeam(pages, target);
    if (index >= 0) setPageIndex(index);
  }, [focusTeamId, effectsKey]);

  // Rotação automática, pausada durante banner e efeitos.
  useEffect(() => {
    if (isMobile || pages.length <= 1 || focusTeamId || fresh) return undefined;

    const timer = setInterval(
      () => setPageIndex(index => (index + 1) % pages.length),
      ROTATE_EVERY_MS,
    );
    return () => clearInterval(timer);
  }, [isMobile, pages.length, focusTeamId, fresh]);

  const safeIndex = Math.min(pageIndex, pages.length - 1);

  if (snapshot.ranking.length === 0) {
    return (
      <Centered>
        <p>Nenhuma equipe inscrita ainda.</p>
      </Centered>
    );
  }

  // Backend antigo não manda a arena no ranking: usa a da última rodada.
  const arenaFromRounds = new Map<string, string>();
  [...snapshot.last_rounds].reverse().forEach(round => {
    if (round.arena_name) arenaFromRounds.set(round.team_id, round.arena_name);
  });

  const toRows = (entries: IBoardSnapshot['ranking'][number]['entries']) =>
    entries.map<IRankingRow>(entry => ({
      id: entry.team_id,
      position: entry.position,
      team_name: entry.team_name,
      school_name: entry.school_name,
      arena_name: entry.arena_name ?? arenaFromRounds.get(entry.team_id),
      points: entry.points,
      arena_points: entry.rounds_total,
      presentation_points: entry.presentation_scored
        ? entry.presentation_total
        : null,
      best_round_time: entry.best_round_time,
    }));

  /** Selos entram com mola: a mudança "salta" para quem olha de longe. */
  const popIn = (key: string, node: React.ReactNode) => (
    <motion.span
      key={`${key}:${effectsKey}`}
      style={{ display: 'inline-block' }}
      initial={reduceMotion ? { opacity: 0 } : { opacity: 0, scale: 0.3 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ type: 'spring', bounce: 0.5, duration: 0.6, delay: 0.5 }}
    >
      {node}
    </motion.span>
  );

  const renderBadge = (row: IRankingRow) => {
    if (!fresh) return null;
    if (effects.newLeaders.includes(row.id)) {
      return popIn(
        row.id,
        <Badge $tone="leader">
          <TrophyIcon strokeWidth={2.5} />
          NOVO LÍDER
        </Badge>,
      );
    }
    const moved = effects.movements[row.id];
    if (moved > 0)
      return popIn(
        row.id,
        <Badge $tone="up" aria-label={`Subiu ${moved}`}>
          <ArrowUpIcon aria-hidden strokeWidth={3} />
          {moved}
        </Badge>,
      );
    if (moved < 0)
      return popIn(
        row.id,
        <Badge $tone="down" aria-label={`Caiu ${Math.abs(moved)}`}>
          <ArrowDownIcon aria-hidden strokeWidth={3} />
          {Math.abs(moved)}
        </Badge>,
      );
    return null;
  };

  const renderPosition = (row: IRankingRow) =>
    row.position === null ? (
      '—'
    ) : (
      <PositionDisc $position={row.position}>{row.position}</PositionDisc>
    );

  const renderPoints = (row: IRankingRow) => (
    <AnimatedPoints
      key={`${row.id}:${effectsKey}`}
      value={row.points}
      from={fresh ? effects.scoreChanges[row.id]?.from : undefined}
    />
  );

  const rowClassName = (row: IRankingRow): string | undefined => {
    if (!fresh) return undefined;
    if (effects.newLeaders.includes(row.id)) return 'board-leader';
    if (effects.scoreChanges[row.id]) return 'board-scored';
    return undefined;
  };

  const common = {
    variant: 'board' as const,
    animateLayout: true,
    // Etapa nacional: arena + apresentação técnica (ou "pendente").
    showBreakdown: snapshot.tournament.stage === 'national',
    // Tempo da melhor rodada à vista explica um empate de pontos.
    showTieTime: true,
    renderBadge,
    renderPoints,
    renderPosition,
    rowClassName,
  };

  if (isMobile) {
    const selected =
      snapshot.ranking.find(g => groupKey(g) === mobileGroup) ??
      snapshot.ranking[0];

    return (
      <RankingArea>
        <GroupChips role="tablist">
          {snapshot.ranking.map(group => (
            <button
              key={groupKey(group)}
              type="button"
              aria-pressed={groupKey(group) === groupKey(selected)}
              onClick={() => setMobileGroup(groupKey(group))}
            >
              {groupTitle(group)}
            </button>
          ))}
        </GroupChips>
        <TournamentRanking
          {...common}
          title={trophyTitle(groupTitle(selected))}
          rows={toRows(selected.entries)}
        />
      </RankingArea>
    );
  }

  const rotating = pages.length > 1 && !focusTeamId && !fresh;

  return (
    <RankingArea>
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={safeIndex}
          initial={reduceMotion ? { opacity: 0 } : { opacity: 0, x: 80 }}
          animate={{ opacity: 1, x: 0 }}
          exit={reduceMotion ? { opacity: 0 } : { opacity: 0, x: -80 }}
          transition={{ type: 'spring', bounce: 0, duration: 0.55 }}
        >
          {pages[safeIndex].map(item => (
            <TournamentRanking
              {...common}
              key={`${item.key}:${item.part}`}
              title={trophyTitle(
                `${groupTitle(item.group)}${
                  item.parts > 1 ? ` (${item.part}/${item.parts})` : ''
                }`,
              )}
              rows={toRows(item.entries)}
            />
          ))}
        </motion.div>
      </AnimatePresence>
      {pages.length > 1 && (
        <PageNav aria-label={`Página ${safeIndex + 1} de ${pages.length}`}>
          {pages.map((_, index) => (
            <PageDot key={index} $active={index === safeIndex}>
              {index === safeIndex && (
                // Enche até a próxima troca; parada (cheia) durante efeitos.
                <motion.span
                  key={`${safeIndex}:${rotating}`}
                  initial={{ scaleX: rotating ? 0 : 1 }}
                  animate={{ scaleX: 1 }}
                  transition={{
                    duration: rotating ? ROTATE_EVERY_MS / 1000 : 0,
                    ease: 'linear',
                  }}
                />
              )}
            </PageDot>
          ))}
        </PageNav>
      )}
    </RankingArea>
  );
};

export default RankingBoard;
